import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, ArrowRight, ArrowLeft, ShieldCheck, Money, LockKey, UsersThree, BookOpenText, Code, YoutubeLogo } from "@phosphor-icons/react";
import { getToken, api } from "@/lib/api";
import { setRefSig, getVisitorSig } from "@/lib/attribution";
import { useViewerSurface } from "@/lib/useViewerSurface";
import ReportContactModal from "@/components/ReportContactModal";
import UniqueWaysModal from "@/components/UniqueWaysModal";
import LandingPricing from "@/components/LandingPricing";
import WhatIsASilo from "@/components/WhatIsASilo";
import AudienceSwitch from "@/components/AudienceSwitch";
import { AUDIENCES, TONE_RGB } from "@/lib/audiences";
import TrialDoorModal from "@/components/TrialDoorModal";
import { usePublicPricing, fmt$ } from "@/lib/usePublicPricing";
import ShareMySiloButton from "@/components/ShareMySiloButton";

const Section = ({ children, className = "" }) => (
  <section className={`max-w-6xl mx-auto px-6 ${className}`}>{children}</section>
);

// Oct 2026 — two tiny renderers so `lib/audiences.js` can stay plain strings.
// `heroStrong` names the phrase to highlight; `{use}` marks where the link to
// the Unique Ways modal belongs inside a value card.
const withStrong = (text, strong, cls) => {
  if (!strong || !text.includes(strong)) return text;
  const [before, after] = text.split(strong);
  return <>{before}<span className={cls}>{strong}</span>{after}</>;
};

const withUseLink = (text, onUse, cls) => {
  if (!text.includes("{use}")) return text;
  const [before, after] = text.split("{use}");
  return (
    <>
      {before}
      <button type="button" onClick={onUse} data-testid="landing-ways-to-use-link" className={cls}>use</button>
      {after}
    </>
  );
};

// Each telling fades its words in while the layout holds still — Randy:
// *"almost as if it's just the words changing."*
const Swap = ({ k, children, className = "" }) => (
  <motion.div
    key={k}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.28, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   Oct 2026 (Randy pt-sunrise-landing) — SUNRISE THEME, ONE SWITCH.

   The page STRUCTURE is untouched: same sections, same order, same copy, same
   data-testids. Only the class strings move, and they move through this one
   table. Flip SUNRISE_ON to false and the original paper page returns exactly
   as it was. `?classic=1` does the same thing live, with no deploy, so the two
   can be compared side by side in two tabs.

   Palette is sampled from the photograph rather than invented: sun core
   #FFC24A, sky amber #F2A94E, deep sky #0B1320, forest black #0A0D0F, warm
   off-white #F5F1E8 for type.
   ──────────────────────────────────────────────────────────────────────────── */
const SUNRISE_ON = true;

// Value-card icons, named in `lib/audiences.js` so the copy file stays free of
// imports.
const VALUE_ICONS = { shield: ShieldCheck, money: Money, sparkle: Sparkle, lock: LockKey, code: Code };

// 24x16 blur of the hero photo, so the fold paints warm instead of black while
// the real image streams in. Costs 940 bytes and no request.
const LQIP = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDABMNDhEODBMRDxEVFBMXHTAfHRoaHToqLCMwRT1JR0Q9Q0FMVm1dTFFoUkFDX4JgaHF1e3x7SlyGkIV3j214e3b/2wBDARQVFR0ZHTgfHzh2T0NPdnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnb/wAARCAAQABgDASIAAhEBAxEB/8QAHwAAAQUBAQEBAQEAAAAAAAAAAAECAwQFBgcICQoL/8QAtRAAAgEDAwIEAwUFBAQAAAF9AQIDAAQRBRIhMUEGE1FhByJxFDKBkaEII0KxwRVS0fAkM2JyggkKFhcYGRolJicoKSo0NTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqDhIWGh4iJipKTlJWWl5iZmqKjpKWmp6ipqrKztLW2t7i5usLDxMXGx8jJytLT1NXW19jZ2uHi4+Tl5ufo6erx8vP09fb3+Pn6/8QAHwEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoL/8QAtREAAgECBAQDBAcFBAQAAQJ3AAECAxEEBSExBhJBUQdhcRMiMoEIFEKRobHBCSMzUvAVYnLRChYkNOEl8RcYGRomJygpKjU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6goOEhYaHiImKkpOUlZaXmJmaoqOkpaanqKmqsrO0tba3uLm6wsPExcbHyMnK0tPU1dbX2Nna4uPk5ebn6Onq8vP09fb3+Pn6/9oADAMBAAIRAxEAPwDFtVQEbmUD610NhFbtgFkrjkfy8E8/jV231LyWDKoJ96U5Sa0ZUFFPVHYXMFtFjLxgnjk9aK4rUtSmupFYNtAxwpxz60VkoTa1kac0V0P/2Q==";

const PAPER = {
  bg: false,
  page: "min-h-screen bg-[#FDFCF8] text-[#1C1B1A]",
  nav: "border-b hairline bg-[#FDFCF8]/80 backdrop-blur-sm sticky top-0 z-20",
  logoDot: "w-9 h-9 rounded-full bg-[#1C1B1A] text-[#FDFCF8] flex items-center justify-center",
  logoSub: "text-[10px] text-[#6B6A68] tracking-[0.25em] uppercase mt-1",
  navBack: "text-sm px-4 py-2 rounded-md bg-[#F0F1EE] text-[#1C1B1A] hover:bg-[#E5E5E0] transition-colors flex items-center gap-1.5",
  navSignIn: "text-sm px-4 py-2 rounded-md text-[#6B6A68] hover:text-[#1C1B1A] hover:bg-[#F0F1EE] transition-colors",
  navSignUp: "text-sm px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] transition-colors flex items-center gap-1.5",
  eyebrow: "inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#6B6A68] mb-8",
  heroPanel: "max-w-4xl",
  accent: "text-[#4A5D4E]",
  heroBody: "mt-8 text-[#6B6A68] text-lg leading-relaxed max-w-2xl",
  heroStrong: "text-[#1C1B1A]",
  heroBack: "px-7 py-4 rounded-md bg-[#F0F1EE] text-[#1C1B1A] hover:bg-[#E5E5E0] transition-colors flex items-center gap-2 text-base shadow-sm w-fit",
  ctaPrimary: "px-7 py-4 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] transition-colors flex items-center gap-2 text-base shadow-sm",
  ctaSecondary: "px-7 py-4 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#1C1B1A] transition-colors text-base",
  card: "bg-white border hairline rounded-xl p-6",
  cardIcon: "w-11 h-11 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center mb-4",
  cardTitle: "font-serif-display text-xl text-[#1C1B1A]",
  cardBody: "mt-2 text-[#6B6A68] text-sm leading-relaxed",
  inlineLink: "text-[#4A5D4E] underline underline-offset-2 hover:text-[#1C1B1A] font-medium transition-colors",
  sectionEyebrow: "text-xs uppercase tracking-[0.25em] text-[#6B6A68] mb-3",
  h2: "font-serif-display text-4xl sm:text-5xl text-[#1C1B1A] max-w-2xl",
  stepNum: "text-[#C16E5A] text-sm font-medium tracking-wide",
  stepBody: "text-[#6B6A68] mt-2 text-sm leading-relaxed",
  tools: "bg-[#1C1B1A] text-[#FDFCF8] rounded-2xl p-10 md:p-14",
  toolsEyebrow: "flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FDFCF8]/60 mb-3",
  toolsAccent: "text-[#C16E5A]",
  toolsBody: "mt-5 text-[#FDFCF8]/70 max-w-2xl leading-relaxed",
  toolsTile: "border border-[#FDFCF8]/15 rounded-lg p-5",
  toolsTileLabel: "text-[#C16E5A] text-xs tracking-wide uppercase mb-2",
  ctaIcon: "mx-auto text-[#4A5D4E]",
  heroSubCta: "mt-1 text-xs text-[#6B6A68]",
  // Oct 2026 (Randy, fan-first pass) — the three doorways.
  doorFan: "group rounded-xl border-2 border-[#4A5D4E]/35 bg-white p-5 hover:border-[#4A5D4E] hover:shadow-md transition-all",
  doorCreator: "group rounded-xl border hairline bg-white p-5 hover:border-[#1C1B1A] hover:shadow-md transition-all",
  doorEyebrow: "flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2",
  doorTitle: "font-serif-display text-2xl text-[#1C1B1A] flex items-center justify-between gap-2",
  doorBody: "mt-2 text-sm text-[#6B6A68] leading-relaxed",
  doorTrial: "inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#4A5D4E]/35 bg-[#F0F1EE] text-[#3A4A3E] text-xs font-medium hover:bg-[#E5E5E0] hover:border-[#4A5D4E] transition-colors",
  doorGhost: "inline-flex items-center gap-1.5 px-4 py-2 rounded-full border hairline bg-white text-[#6B6A68] text-xs hover:text-[#1C1B1A] hover:border-[#1C1B1A] transition-colors",
  demoPill: "inline-flex items-center px-5 py-2.5 rounded-full bg-[#1C1B1A] text-[#FDFCF8] text-sm hover:bg-[#3A4A3E] transition-colors shadow-sm",
  demoSub: "mt-1.5 text-[11px] text-[#6B6A68] tracking-wide",
  fineprint: "mt-4 text-xs text-[#6B6A68]",
  footer: "border-t hairline",
  footerRow: "py-8 text-xs text-[#6B6A68] flex flex-wrap items-center justify-between gap-3",
  footerLink: "hover:text-[#1C1B1A] transition-colors",
};

const SUNRISE = {
  bg: true,
  // `relative isolate` is load-bearing: it makes this div a stacking context so
  // the backdrop's negative z-index is CONTAINED here. Without it the layer
  // paints behind `#root { background: #FDFCF8 }` and the photo never appears.
  // Which is also why this variant carries no background colour of its own:
  // the backdrop supplies the base.
  page: "min-h-screen text-[#F5F1E8] relative isolate",
  nav: "border-b border-[#F5F1E8]/10 bg-[#070B11]/70 backdrop-blur-md sticky top-0 z-20",
  logoDot: "w-9 h-9 rounded-full bg-gradient-to-br from-[#FFC24A] to-[#E07B2C] text-[#1A1206] flex items-center justify-center shadow-[0_0_24px_rgba(255,194,74,0.35)]",
  logoSub: "text-[10px] text-[#F5F1E8]/55 tracking-[0.25em] uppercase mt-1",
  navBack: "text-sm px-4 py-2 rounded-md bg-[#F5F1E8]/10 text-[#F5F1E8] hover:bg-[#F5F1E8]/20 transition-colors flex items-center gap-1.5 backdrop-blur-sm",
  navSignIn: "text-sm px-4 py-2 rounded-md text-[#F5F1E8]/70 hover:text-[#FFC24A] hover:bg-[#F5F1E8]/5 transition-colors",
  navSignUp: "text-sm px-5 py-2.5 rounded-md bg-[#F5EFE2] text-[#14110C] hover:bg-[#FFC24A] transition-colors flex items-center gap-1.5 shadow-[0_6px_24px_rgba(0,0,0,0.45)]",
  eyebrow: "inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FFC24A]/85 mb-8",
  // The hero sits directly on the photograph, so it gets its own glass so the
  // type never has to fight the sun.
  // `backdrop-brightness` is what makes the glass self-correcting. A flat tint
  // fails on this photo: the same value that looks right over the forest leaves
  // white type invisible over the sun. Multiplying what is behind the glass
  // darkens the blown-out sun and the dark forest by the same proportion, so
  // the type is legible wherever a card happens to land.
  heroPanel: "max-w-4xl rounded-3xl border border-[#F5F1E8]/12 bg-[#060A12]/22 backdrop-blur-2xl backdrop-brightness-[0.46] backdrop-saturate-150 p-8 sm:p-12 shadow-[0_24px_80px_-24px_rgba(0,0,0,0.7)]",
  accent: "text-[#FFC24A] not-italic font-normal",
  heroBody: "mt-8 text-[#E7E2D6]/85 text-lg leading-relaxed max-w-2xl",
  heroStrong: "text-[#FFC24A]",
  heroBack: "px-7 py-4 rounded-md bg-[#F5F1E8]/10 text-[#F5F1E8] hover:bg-[#F5F1E8]/20 transition-colors flex items-center gap-2 text-base backdrop-blur-sm w-fit",
  ctaPrimary: "px-7 py-4 rounded-md bg-[#F5EFE2] text-[#14110C] hover:bg-[#FFC24A] transition-colors flex items-center gap-2 text-base shadow-[0_10px_40px_-8px_rgba(255,194,74,0.5)]",
  ctaSecondary: "px-7 py-4 rounded-md border border-[#F5F1E8]/25 bg-[#F5F1E8]/5 text-[#F5F1E8] hover:border-[#FFC24A] hover:text-[#FFC24A] transition-colors text-base backdrop-blur-sm",
  card: "rounded-xl p-6 border border-[#F5F1E8]/12 bg-[#070B11]/25 backdrop-blur-2xl backdrop-brightness-[0.38] backdrop-saturate-150 hover:border-[#FFC24A]/45 transition-colors shadow-[0_18px_50px_-20px_rgba(0,0,0,0.75)]",
  cardIcon: "w-11 h-11 rounded-md bg-[#FFC24A]/12 text-[#FFC24A] flex items-center justify-center mb-4 border border-[#FFC24A]/20",
  cardTitle: "font-serif-display text-xl text-[#F5F1E8]",
  cardBody: "mt-2 text-[#E7E2D6]/70 text-sm leading-relaxed",
  inlineLink: "text-[#FFC24A] underline underline-offset-2 hover:text-[#F5F1E8] font-medium transition-colors",
  sectionEyebrow: "text-xs uppercase tracking-[0.3em] text-[#FFC24A] mb-3 [text-shadow:0_1px_12px_rgba(0,0,0,0.95)]",
  h2: "font-serif-display text-4xl sm:text-5xl text-[#F5F1E8] max-w-2xl [text-shadow:0_2px_30px_rgba(0,0,0,0.85)]",
  stepNum: "text-[#FFC24A] text-sm font-medium tracking-wide",
  stepBody: "text-[#E7E2D6]/70 mt-2 text-sm leading-relaxed",
  tools: "rounded-2xl p-10 md:p-14 border border-[#FFC24A]/18 bg-[#05080C]/30 backdrop-blur-2xl backdrop-brightness-[0.34] backdrop-saturate-150 text-[#F5F1E8] shadow-[0_30px_90px_-30px_rgba(0,0,0,0.9)]",
  toolsEyebrow: "flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FFC24A]/75 mb-3",
  toolsAccent: "text-[#FFC24A]",
  toolsBody: "mt-5 text-[#E7E2D6]/70 max-w-2xl leading-relaxed",
  toolsTile: "border border-[#F5F1E8]/12 rounded-lg p-5 bg-[#F5F1E8]/[0.03]",
  toolsTileLabel: "text-[#FFC24A] text-xs tracking-wide uppercase mb-2",
  ctaIcon: "mx-auto text-[#FFC24A] drop-shadow-[0_2px_14px_rgba(0,0,0,0.9)]",
  heroSubCta: "mt-1 text-xs text-[#E7E2D6]/75 [text-shadow:0_1px_10px_rgba(0,0,0,0.95)]",
  // Oct 2026 (Randy, fan-first pass) — the three doorways. Same glass as the
  // hero panel they sit inside, with the fan door carrying the amber edge so
  // the free way in is the one the eye lands on first.
  doorFan: "group rounded-xl p-5 border border-[#FFC24A]/45 bg-[#FFC24A]/[0.06] backdrop-blur-xl hover:bg-[#FFC24A]/[0.12] hover:border-[#FFC24A]/75 transition-colors",
  doorCreator: "group rounded-xl p-5 border border-[#F5F1E8]/18 bg-[#F5F1E8]/[0.05] backdrop-blur-xl hover:border-[#F5F1E8]/40 hover:bg-[#F5F1E8]/[0.09] transition-colors",
  doorEyebrow: "flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#FFC24A]/85 mb-2",
  doorTitle: "font-serif-display text-2xl text-[#F5F1E8] flex items-center justify-between gap-2",
  doorBody: "mt-2 text-sm text-[#E7E2D6]/75 leading-relaxed",
  doorTrial: "inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#A8C0A2]/45 bg-[#A8C0A2]/[0.10] text-[#E7E2D6] text-xs font-medium hover:bg-[#A8C0A2]/20 hover:border-[#A8C0A2]/80 hover:text-[#F5F1E8] transition-colors backdrop-blur-sm",
  doorGhost: "inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#F5F1E8]/20 bg-[#F5F1E8]/[0.04] text-[#E7E2D6]/80 text-xs hover:text-[#FFC24A] hover:border-[#FFC24A]/45 transition-colors backdrop-blur-sm",
  demoPill: "inline-flex items-center px-5 py-2.5 rounded-full bg-[#F5EFE2] text-[#14110C] text-sm hover:bg-[#FFC24A] transition-colors shadow-[0_10px_40px_-10px_rgba(255,194,74,0.55)]",
  demoSub: "mt-1.5 text-[11px] text-[#E7E2D6]/70 tracking-wide [text-shadow:0_1px_10px_rgba(0,0,0,0.95)]",
  fineprint: "mt-4 text-xs text-[#E7E2D6]/80 [text-shadow:0_1px_10px_rgba(0,0,0,0.95)]",
  footer: "border-t border-[#F5F1E8]/10 bg-[#070B11]/60 backdrop-blur-md",
  footerRow: "py-8 text-xs text-[#E7E2D6]/60 flex flex-wrap items-center justify-between gap-3",
  footerLink: "hover:text-[#FFC24A] transition-colors",
};

// Fixed photographic layer. A real fixed <img> rather than
// `background-attachment: fixed`, which iOS Safari has never supported
// properly and which would judder the whole page on his phone.
const SunriseBackdrop = ({ tone = "anyone" }) => (
  <div className="fixed inset-0 -z-10 overflow-hidden" aria-hidden="true" data-testid="landing-sunrise-backdrop">
    <div className="absolute inset-0 bg-[#0A0D0F]" />
    <div className="absolute inset-0 bg-cover bg-center scale-105 blur-xl" style={{ backgroundImage: `url(${LQIP})` }} />
    <img
      src="/brand/sunrise-1536.jpg"
      srcSet="/brand/sunrise-960.webp 960w, /brand/sunrise-1536.webp 1536w"
      sizes="100vw"
      alt=""
      fetchpriority="high"
      decoding="async"
      className="absolute inset-0 w-full h-full object-cover object-[50%_45%] brightness-[0.82] saturate-[1.06]"
    />
    {/* Readability scrim. Deliberately near-transparent through the middle so
        the sun still reads as a sunrise instead of a grey wash: the hero's own
        frosted panel is what guarantees contrast for the type. */}
    <div className="absolute inset-0 bg-gradient-to-b from-[#04070E]/62 via-[#050810]/12 to-[#07050A]/74" />
    {/* Warm bloom lifted off the sun, so the amber type has something to echo. */}
    <div className="absolute right-[-10%] top-[22%] w-[55vw] h-[55vw] rounded-full bg-[#FFB347]/10 blur-[120px]" />
    {/* Oct 2026 (Randy) — the audience tone. Same photograph, different light in
        the room. Two layers, because one `mix-blend-color` layer over an already
        warm sunrise simply vanished: *"the red is not back."* The blend layer
        shifts the hue, the plain wash on top makes sure you can SEE it. Literal
        inline colours, so nothing here depends on a class being generated.
        TONE_RGB lives in `lib/audiences.js`. */}
    <div
      data-testid="landing-tone-overlay"
      className="absolute inset-0 mix-blend-color transition-all duration-700 ease-out"
      style={{ backgroundColor: TONE_RGB[tone]?.blend || "transparent" }}
    />
    <div
      data-testid="landing-tone-wash"
      className="absolute inset-0 transition-all duration-700 ease-out"
      style={{ backgroundColor: TONE_RGB[tone]?.wash || "transparent" }}
    />
  </div>
);

export default function Landing() {
  // /m/:refCode/:siloSlug — dynamic per-creator marketing page. Params absent
  // on plain `/` so we ask the backend for the best populated silo to demo;
  // falls back to "cosmic-revelation" if the API can't find one. This auto-
  // heals when silos get renamed or new ones are added.
  const { refCode: routeRefCode, siloSlug: routeSiloSlug } = useParams();
  const [featuredSlug, setFeaturedSlug] = useState("cosmic-revelation");
  const [contactOpen, setContactOpen] = useState(false);
  // Oct 2026 (HOMEPAGE_AUDIENCE_PATHS.md) — which telling is on screen. One
  // address, no route, no persistence: Randy was emphatic that Anyone is the
  // default and the page must never open as anything else.
  const [audience, setAudience] = useState("anyone");
  const A = AUDIENCES[audience];
  const [showWays, setShowWays] = useState(
    // Oct 4, 2026 (Randy) — the widget's "What is My Silo?" explainer links
    // here for the full list. It cannot import lib/waysToUse (embed.html is a
    // static file, not React), so it deep-links `?ways=1` instead — and does it
    // through /m/<refCode>/<slug>, which keeps the widget owner's referral
    // credit on the visit.
    () => {
      try { return new URLSearchParams(window.location.search).get("ways") === "1"; }
      catch { return false; }
    },
  );
  useEffect(() => {
    if (routeSiloSlug) return; // marketing-page route already specifies it
    let cancel = false;
    api.get("/marketing/featured-silo")
      .then((r) => { if (!cancel && r.data?.slug) setFeaturedSlug(r.data.slug); })
      .catch(() => { /* keep fallback */ });
    return () => { cancel = true; };
  }, [routeSiloSlug]);
  const demoSlug = routeSiloSlug || featuredSlug;

  // Oct 2026 (Randy, fan-first pass) — the demo roster behind "See live
  // silos". Two doorways (a deep corpus and a YouTube twin) resolved live, so
  // a rename degrades to one pill rather than a 404. The featured slug stays
  // the fallback when the roster is empty.
  const [demos, setDemos] = useState([]);
  const [demosOpen, setDemosOpen] = useState(false);
  const [trialDoor, setTrialDoor] = useState(false);
  useEffect(() => {
    let cancel = false;
    api.get("/marketing/demo-silos")
      .then((r) => { if (!cancel) setDemos(r.data?.silos || []); })
      .catch(() => { /* fallback below */ });
    return () => { cancel = true; };
  }, []);
  const demoList = demos.length
    ? demos
    : [{ slug: demoSlug, name: "See a live silo", subtitle: "A real, answering corpus" }];

  // Oct 2026 — `?classic=1` forces the original paper page. Two tabs, same
  // build, instant A/B, and a rollback that needs no deploy.
  const [classic] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("classic") === "1"; }
    catch { return false; }
  });
  const T = (SUNRISE_ON && !classic) ? SUNRISE : PAPER;
  const pricing = usePublicPricing();

  // Sprint Feb 8, 2026 — use the canonical useViewerSurface() so the
  // profile-shortcut label is correct for ALL roles (Super Admin,
  // Affiliate Creator, Creator, New Creator, Fan). Previously this only
  // checked is_paid_creator + super_admin and missed affiliate-creators
  // entirely, which made an Affiliate Creator visiting the landing page
  // see "Back to my fan portal" — wrong on every level.
  const { isOperator, isAnon } = useViewerSurface();
  const loggedIn = !!getToken();
  const loggedInUser = loggedIn && !isAnon;
  const profileLabel = !loggedIn || isAnon
    ? null
    : isOperator
      ? "Back to creator dashboard"
      : "Back to my fan portal";
  const profileHref = isOperator ? "/dashboard" : "/fan-portal";

  // iter-31 + P0 (Feb 7, 2026): honor ?ref= on the landing page. Stash the
  // sig in localStorage so signup forwards it, AND fire /referrals/accept so
  // the inviter sees the visitor in their Ghost Report immediately (rather
  // than having to wait for signup).
  //
  // The sessionStorage→localStorage migration in /lib/attribution makes this
  // seed survive: tab switches, browser restarts, private mode resumes.
  // Latest-seed-wins is enforced server-side in record_pending — so a fresh
  // ?ref= here correctly demotes any prior pending row for this visitor.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      // /m/:refCode/:siloSlug takes priority over ?ref= so a marketing
      // link click cleanly stamps the right inviter — but we still honor
      // ?ref= when present (e.g. legacy share copies). The "_" sentinel
      // (used by PublicSilo when there's no owner refCode) is treated as
      // "no ref" so attribution stays untouched.
      const routeRef = routeRefCode && routeRefCode !== "_" ? routeRefCode : null;
      const ref = routeRef || params.get("ref");
      if (ref) {
        setRefSig(ref);
        const visitor_sig = getVisitorSig();
        if (visitor_sig) {
          // `ref_source` is descriptive only (the Silo Anywhere widget's
          // Powered-by link sets it). It never changes which inviter wins —
          // latest-seed-wins still decides that, server-side.
          const source = params.get("ref_source") || null;
          api.post("/referrals/accept", { referrer_sig: ref, visitor_sig, silo_id: null, source }).catch(() => {});
        }
      }
    } catch { /* ignore */ }
  }, [routeRefCode]);

  return (
    <div className={T.page} data-testid="landing-page">
      {T.bg && <SunriseBackdrop tone={audience} />}
      {/* Nav */}
      <nav className={T.nav}>
        <Section className="py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className={T.logoDot}>
              <Sparkle size={18} weight="duotone" />
            </div>
            <div>
              <div className="font-serif-display text-xl leading-none">My Silo</div>
              <div className={T.logoSub}>Personal Digital Twin</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            {loggedInUser ? (
              <Link to={profileHref} data-testid="nav-back-to-profile" className={T.navBack}>
                <ArrowLeft size={14} /> <span className="hidden sm:inline">{profileLabel}</span><span className="sm:hidden">My profile</span>
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className={T.navSignIn}>Sign in</Link>
                <Link to="/signup?as=creator" data-testid="nav-signup" className={T.navSignUp}>
                  Start your silo <ArrowRight size={14} />
                </Link>
              </>
            )}
          </div>
        </Section>
      </nav>

      {/* Hero — Oct 2026: Randy pulled the three big audience cards off the
          right ("I actually don't like the huge 3 cards") in favour of one quiet
          line inside the top of the panel: *My silo for: YouTubers · Businesses
          · Anyone*. Descriptions only on hover. */}
      <Section className="pt-24 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className={T.heroPanel}
          /* The YouTube telling sits on a darker canvas — matched to the value
             cards below — so a full page of red doesn't wash the copy out. */
          style={T.bg && audience === "youtube" ? {
            backgroundColor: "rgba(7,11,17,0.25)",
            backdropFilter: "blur(24px) brightness(0.38) saturate(1.5)",
            WebkitBackdropFilter: "blur(24px) brightness(0.38) saturate(1.5)",
          } : undefined}
        >
          <AudienceSwitch value={audience} onChange={setAudience} dark={!!T.bg} />
          <Swap k={audience}>
            <div className={T.eyebrow}>
              <BookOpenText size={16} weight="duotone" /> {A.eyebrow}
            </div>
            {/* Nov 2026 (Randy) — the mark moved OUT of the left column and on
                to the end of the headline's last line: *"For your channel
                (symbol here) — move the logo."* Beside the text it narrowed the
                column enough to wrap this headline to four lines while the
                other two faces ran at two, which is what made the YouTube face
                sit lower than the rest.

                The headline reserves TWO lines on every face (in em, so it
                tracks the font size at each breakpoint) — *"it needs to feel
                solid, it doesn't move when you flick between the faces."*
                Nov 3 2026 — it reserved three, which left a dead line of
                whitespace above the paragraph on all three faces: *"an overly
                large unneeded gap."* Every face writes two lines, so the third
                was pure padding. */}
            <div>
              <h1 className={`font-serif-display leading-[1.04] tracking-tight [font-variant-numeric:lining-nums] min-h-[2.12em] ${A.h1Size || "text-5xl sm:text-6xl lg:text-7xl"}`}>
                {A.h1.post ? (
                  <>{A.h1.pre} <em className={T.accent}>{A.h1.accent}</em><br />{A.h1.post}</>
                ) : (
                  <>{A.h1.pre}<br /><em className={T.accent}>{A.h1.accent}</em></>
                )}
                {A.heroMark === "youtube" && (
                  <YoutubeLogo
                    weight="fill"
                    className="inline-block align-[-0.18em] ml-3 w-[1.1em] h-[1.1em] text-[#FF0033] drop-shadow-[0_3px_16px_rgba(0,0,0,0.65)]"
                    data-testid="hero-mark-youtube"
                  />
                )}
              </h1>
            </div>
            {/* Oct 2026 (Randy): the YouTube and Business headlines he wrote run
                to a second breath.
                Nov 2026 — *"the font and spacing on the business and youtube
                don't match the 'Anyone' face. Preferred — anyone face."* It was
                set in serif at heading weight, so those two faces opened with a
                typeface the Anyone face never shows. Now it carries the same
                body treatment, which also makes the first line of text sit the
                same distance under every headline. */}
            {A.h1Sub && (
              <p className={T.heroBody} data-testid="hero-h1-sub">
                {A.h1Sub}
              </p>
            )}
            <p className={T.heroBody}>
              {withStrong(A.heroBody, A.heroStrong, T.heroStrong)}
            </p>
          </Swap>
          {/* Oct 2026 (Randy) — the same small answer that sits under the
              pricing heading, because this is where the question first lands. */}
          <div className="mt-3">
            <WhatIsASilo dark={!!T.bg} variant="hero" testid="hero-what-is-a-silo" />
          </div>

          {/* Live proof, back where it belongs. Oct 2026 (Randy): *"move the
              'see a live silo' button just below the heading text… make the
              button larger and more prominent akin to the way it originally
              was."* The doorways, the trial and its fineprint stay exactly
              where they were, underneath.

              YouTube gets two buttons of equal size: his own twin, and his
              actual channel, because the replies are readable in the wild.
              Business gets nothing — there is no business silo to show yet,
              and the button is absent rather than empty. */}
          {A.demos.kind !== "none" && (
            <div className="mt-7 flex flex-wrap items-center gap-3" data-testid="landing-demo-row">
              {A.demos.kind === "direct" ? (
                <>
                  <Link to={`/s/${A.demos.slug}`} data-testid="hero-cta-demo" className={T.ctaPrimary}>
                    {A.demos.label} <ArrowRight size={16} />
                  </Link>
                  <a
                    href={A.demos.channelHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-demo-channel"
                    className={`${T.ctaSecondary} inline-flex items-center gap-2`}
                  >
                    <YoutubeLogo size={18} weight="duotone" /> {A.demos.channelLabel}
                  </a>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setDemosOpen((v) => !v)}
                    data-testid="hero-cta-demo"
                    className={T.ctaPrimary}
                    aria-expanded={demosOpen}
                  >
                    See a live silo {demosOpen ? "↑" : "→"}
                  </button>
                  {/* They arrive to the RIGHT of the button, in roster order. */}
                  {demosOpen && demoList.map((d, i) => (
                    <motion.div
                      key={d.slug}
                      initial={{ opacity: 0, x: -12, scale: 0.96 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      transition={{ delay: i * 0.1, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="flex flex-col items-center"
                    >
                      <Link to={`/s/${d.slug}`} data-testid={`landing-demo-${d.slug}`} className={T.demoPill}>
                        {d.name}
                      </Link>
                      <span className={T.demoSub}>{d.subtitle}</span>
                    </motion.div>
                  ))}
                </>
              )}
            </div>
          )}
          <div className="mt-10 flex flex-col gap-3">
            {/* iter-35e: when a logged-in user lands here (e.g. via the
                site-wide logo click), give them a one-tap shortcut back to
                their profile. Light gray, same size as the primary CTA below,
                placed ABOVE it. Routes by user type via DashboardGate. */}
            {loggedInUser && (
              <Link to={profileHref} data-testid="hero-back-to-profile" className={T.heroBack}>
                <ArrowLeft size={16} /> {profileLabel}
              </Link>
            )}

            {/* Oct 2026 (Randy, fan-first pass) — THREE doorways, because there
                have always been three kinds of visitor and the page only ever
                spoke to one of them.

                Joining free as a fan is the thing nothing on this page used to
                say out loud: you can be here to read other people's twins, and
                that costs nothing, ever. It goes first and it gets the most
                room.

                "Create a silo" is the original door, restored — it leads to
                signup and then to /start, where the two ways to begin are laid
                out. It never drops a would-be creator into a social feed again.

                The trial is the third door and deliberately the smallest: a
                button that explains itself when tapped. It is an option, not
                the entrance. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="landing-doorways">
              <Link to="/signup?as=fan" data-testid="hero-cta-fan" className={T.doorFan}>
                <Swap k={audience}>
                  <div className={T.doorEyebrow}>
                    <UsersThree size={13} weight="duotone" /> {A.doors.fan.eyebrow}
                  </div>
                  <div className={T.doorTitle}>
                    {A.doors.fan.title} <ArrowRight size={16} className="shrink-0" />
                  </div>
                  <p className={T.doorBody}>{A.doors.fan.body}</p>
                </Swap>
              </Link>

              <Link to="/signup?as=creator" data-testid="hero-cta-signup" className={T.doorCreator}>
                <Swap k={audience}>
                  <div className={T.doorEyebrow}>
                    <BookOpenText size={13} weight="duotone" /> {A.doors.creator.eyebrow}
                  </div>
                  <div className={T.doorTitle}>
                    {A.doors.creator.title} <ArrowRight size={16} className="shrink-0" />
                  </div>
                  <p className={T.doorBody}>{A.doors.creator.body}</p>
                </Swap>
              </Link>
            </div>

            {/* Third door. The live-silo proof moved up under the headline, so
                this row carries the trial and — Nov 2026 (Randy) — the share
                button, in the same pill so neither reads as the main way in.
                He placed the share pill himself, at the far right of this row
                (`ml-auto`), so its right edge lines up with the Create-a-silo
                card above it rather than crowding the trial. */}
            <div className="mt-4 flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={() => setTrialDoor(true)}
                data-testid="hero-cta-trial"
                className={T.doorTrial}
              >
                <Sparkle size={13} weight="duotone" /> 14-day trial
              </button>
              <ShareMySiloButton className={`${T.doorTrial} sm:ml-auto`} testId="hero-cta-share" />
            </div>

            {/* Oct 2026 (TRIAL_PLAN.md §8.1) — the facts a visitor needs before
                clicking, in the order they'd ask them.
                Nov 2026 (Randy) — "card required" dropped: *"they will find the
                card is required naturally when they sign up, no need to jab
                them with it early."* It still appears on the trial door itself,
                which is where the card is actually asked for. */}
            <p className={T.heroSubCta} data-testid="landing-trial-subcta">
              Trial: 14 days of everything · cancel anytime
            </p>
          </div>
        </motion.div>
      </Section>

      {/* Value props */}
      <Section className="pb-24">
        <Swap k={audience} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {A.values.map((v, i) => {
            const Icon = VALUE_ICONS[v.icon] || Sparkle;
            return (
              <motion.div
                key={`${audience}-${i}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className={T.card}
                data-testid={`landing-value-${i}`}
              >
                <div className={T.cardIcon}>
                  <Icon size={22} weight="duotone" />
                </div>
                <h3 className={T.cardTitle}>{v.title}</h3>
                <p className={T.cardBody}>{withUseLink(v.body, () => setShowWays(true), T.inlineLink)}</p>
              </motion.div>
            );
          })}
        </Swap>
      </Section>

      {/* How it works */}
      <Section className="pb-24">
        <Swap k={audience}>
          <div className={T.sectionEyebrow}>{A.stepsEyebrow}</div>
          <h2 className={T.h2}>
            {A.stepsH2.pre} <em className={T.accent}>{A.stepsH2.accent}</em>
          </h2>
          <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {A.steps.map((s) => (
              <li key={s.n} className={T.card} data-testid={`landing-step-${s.n}`}>
                <div className={T.stepNum}>{s.n}</div>
                <h4 className="font-serif-display text-xl mt-2">{s.t}</h4>
                <p className={T.stepBody}>{s.b}</p>
              </li>
            ))}
          </ol>
        </Swap>
      </Section>

      {/* Tools — the one block that changes its subject entirely: the Workbench
          for Anyone, the twin for YouTube, Silo Anywhere for Business. */}
      <Section className="pb-24">
        <div className={T.tools}>
          <Swap k={audience}>
            <div className={T.toolsEyebrow}>
              <Code size={16} weight="duotone" /> {A.tools.eyebrow}
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl max-w-3xl">
              {A.tools.h2.pre} <em className={T.toolsAccent}>{A.tools.h2.accent}</em>
            </h2>
            <p className={T.toolsBody}>{A.tools.body}</p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {A.tools.tiles.map((t, i) => (
                <div key={i} className={T.toolsTile}>
                  <div className={T.toolsTileLabel}>Built-in</div>
                  <div className="text-sm leading-relaxed">{t}</div>
                </div>
              ))}
            </div>
          </Swap>
        </div>
      </Section>

      {/* Pricing (Oct 2026, TRIAL_PLAN.md §8.1 · Phase 9) — sits after the
          tools, before the closing CTA: by here a visitor knows what the thing
          is, so the price is an answer rather than an interruption. Same plans
          and same numbers for every audience; only the framing line moves. */}
      <LandingPricing dark={!!T.bg} audience={audience} intro={A.pricingIntro} />

      {/* CTA */}
      <Section className="pb-32 text-center">
        <UsersThree size={36} weight="duotone" className={T.ctaIcon} />
        <Swap k={audience}>
          <h2 className={`font-serif-display text-4xl sm:text-5xl mt-4 max-w-3xl mx-auto leading-tight ${T.bg ? "[text-shadow:0_2px_30px_rgba(0,0,0,0.85)]" : ""}`}>
            {A.cta.pre}<br />
            <em className={T.accent}>{A.cta.accent}</em>
          </h2>
          <Link to="/signup?as=creator" data-testid="cta-bottom" className={`inline-flex items-center gap-2 mt-10 ${T.ctaPrimary}`}>
            {A.cta.button} <ArrowRight size={16} />
          </Link>
        </Swap>
        {/* The free way in, restated where the page closes — a visitor who read
            all the way down is exactly the person who should be told again
            that they don't have to become a creator to be here. */}
        <div className="mt-4">
          <Link to="/signup?as=fan" data-testid="cta-bottom-fan" className={T.inlineLink}>
            or join free as a fan and browse what's already here →
          </Link>
        </div>
        {/* Oct 2026 (TRIAL_PLAN.md §8.1) — the old line said "Free to start",
            which stopped being true the day the trial started taking a card.
            Every clause below is a term someone could otherwise be surprised
            by: the card, the date, the cancel window, who funds the AI. */}
        <p className={T.fineprint} data-testid="landing-fineprint">
          14 days free, card required. {fmt$(pricing.creator_monthly_fee_cents ?? pricing.platform_fee_cents)}/month
          begins on day 15 — cancel before then and you are charged nothing. Pro tools are unlocked
          during the trial; you are only charged for the ones you choose to keep, and only after you
          confirm. AI usage is funded separately, by your own provider key or platform credit.
          Supporters cover the platform fee on fan payments, so you keep 100% of your price. You own
          your data. <Link to="/terms" className={T.footerLink}>Terms</Link> ·{" "}
          <Link to="/refund-policy" className={T.footerLink} data-testid="landing-refund-link">Refund Policy</Link>
        </p>
      </Section>

      <footer className={T.footer}>
        <Section className={T.footerRow}>
          <div>© {new Date().getFullYear()} My Silo · Personal Digital Twin for experts</div>
          <div className="flex gap-5 items-center">
            <Link to="/terms" data-testid="footer-terms" className={T.footerLink}>Terms</Link>
            <Link to="/privacy" data-testid="footer-privacy" className={T.footerLink}>Privacy</Link>
            <button onClick={() => setContactOpen(true)} data-testid="footer-contact-btn"
              className={T.footerLink}>Contact</button>
          </div>
        </Section>
      </footer>
      <ReportContactModal open={contactOpen} onClose={() => setContactOpen(false)} context="general" />
      {showWays && <UniqueWaysModal onClose={() => setShowWays(false)} />}
      <TrialDoorModal open={trialDoor} onClose={() => setTrialDoor(false)} />
    </div>
  );
}
